import type { AxiosResponse, Method } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RequestClient, createRequestClient } from './api-client';
import { useUserStore, useUserStoreApi } from '../state/user';
import { AnyObject } from 'shared/@types';
import { useLocale } from 'shared/library/i18n/use-locale';
import { ApiRoute } from 'shared/library/api-route';
import { useTranslation } from 'react-i18next';
import { notify } from 'shared/library/notify';
import { useResetAuthenticatedState } from 'shared/library/use-reset-authenticated-state';

export interface KeyValueErrors {
  [key: string]: string[];
}

export interface ApiErrorOptions {
  status?: number;
  errors?: KeyValueErrors;
  responseData?: AnyObject;
  context?: AnyObject;
  report?: boolean;
}

interface State {
  requestsMade: number;
  loaders: number;
  errors: KeyValueErrors;
}

interface ApiConfig {
  notifyOnServerErrors: boolean;
  settleAfterUnmount?: boolean;
}

export default function useApi(config: ApiConfig = { notifyOnServerErrors: true }) {
  const { t } = useTranslation('common');
  const isSubscribed = useRef(false);
  const [state, setState] = useState<State>({
    requestsMade: 0,
    loaders: 0,
    errors: {},
  });
  const apiKey = useUserStore((state) => state.apiKey);
  const listingDraftToken = useUserStore((state) => state.listingDraftToken);
  const userStore = useUserStoreApi();
  const resetAuthenticatedState = useResetAuthenticatedState();
  const { languageCode, countryCode } = useLocale();
  const requestClient = useMemo(
    () =>
      createRequestClient({
        languageCode,
        apiKey,
        headers: {
          'X-Country': countryCode,
          ...(listingDraftToken ? { 'X-Listing-Draft-Token': listingDraftToken } : {}),
        },
      }),
    [apiKey, countryCode, languageCode, listingDraftToken],
  );

  useEffect(() => {
    isSubscribed.current = true;

    return () => {
      isSubscribed.current = false;
    };
  }, []);

  const request: RequestClient = async <T = unknown>(
    route: ApiRoute,
    data?: AnyObject,
    headers?: AnyObject,
    abortController: AbortController = new AbortController(),
  ): Promise<T> => {
    clearErrors();

    const params = route.method === 'get' ? data : undefined;
    data = route.method === 'get' ? undefined : data;

    return sendRequest<T>(route.method, route.url, data, params, headers ?? {}, abortController);
  };

  function sendRequest<T>(
    method: Method,
    url: string,
    data: undefined | object = undefined,
    params: undefined | object = undefined,
    headers: AnyObject,
    abortController: AbortController,
  ): Promise<T> {
    setStartedLoading();

    return new Promise<T>((resolve, reject) => {
      requestClient<T>(
        {
          method: method.toLowerCase() as ApiRoute['method'],
          url,
        } as ApiRoute,
        method.toLowerCase() === 'get'
          ? (params as AnyObject | undefined)
          : (data as AnyObject | undefined),
        headers,
        abortController,
      )
        .then((response) => {
          setFinishedLoading();

          if (!isSubscribed.current && !config.settleAfterUnmount) {
            return;
          }

          resolve(response);
        })
        .catch((error) => {
          setFinishedLoading();

          if (userStore.getState().apiKey !== apiKey) {
            reject(
              new ApiError('Request failed after session changed', {
                status: error?.response?.status,
                report: false,
              }),
            );
            return;
          }

          if (!isSubscribed.current && config.settleAfterUnmount) {
            reject(
              new ApiError('Request failed after leaving screen', {
                status: error?.response?.status,
                report: false,
              }),
            );
            return;
          }

          // Axios marks both AbortController and CancelToken errors with __CANCEL__.
          if (error?.__CANCEL__) {
            reject(new ApiError('Request cancelled', { context: { method, url }, report: false }));
          } else if (!isSubscribed.current) {
            reject(
              new ApiError('Not subscribed to request', {
                context: { method, url },
                report: false,
              }),
            );
          } else {
            const response = error?.response;
            const isJson = response?.headers?.['content-type']?.includes('application/json');
            const parsedErrors = !response
              ? convertToAnyError(t('network_error'))
              : response.status === 401 || response.status === 503 || isJson
                ? parseErrors(response)
                : convertToAnyError(t('unknown_error'));

            if (isSubscribed.current) {
              setState((state) => ({ ...state, errors: parsedErrors }));
            }

            const apiError = new ApiError(Object.values(parsedErrors).join('\n\n'), {
              status: response?.status,
              errors: parsedErrors,
              responseData: response?.data,
              context: { method, url },
            });

            if (config.notifyOnServerErrors) {
              apiError.notifyOnServerError();
            }

            reject(apiError);
          }
        });
    });
  }

  function setStartedLoading() {
    if (!isSubscribed.current) {
      return;
    }

    setState((state) => ({ ...state, loaders: state.loaders + 1 }));
  }

  function setFinishedLoading() {
    if (!isSubscribed.current) {
      return;
    }

    setState((state) => ({
      ...state,
      loaders: state.loaders - 1,
      requestsMade: state.requestsMade + 1,
    }));
  }

  function parseErrors(response: AxiosResponse): KeyValueErrors {
    if (response && response.status === 401) {
      void resetAuthenticatedState();
      return convertToAnyError(t('you_have_been_logged_out'));
    } else if (response && response.status === 503) {
      return convertToAnyError(t('we_are_updating_our_systems'));
    } else if (response && response.status === 422) {
      return response.data.errors || {};
    } else if (response && response.data) {
      return convertToAnyError(response.data.message);
    } else {
      return convertToAnyError(t('unknown_error'));
    }
  }

  function convertToAnyError(error: string): KeyValueErrors {
    return { any: [error] };
  }

  function hasErrors(field: string) {
    return state.errors[field] ?? false;
  }

  function clearErrors(field: string | null = null) {
    if (!isSubscribed.current) {
      return;
    }

    if (!field) {
      setState((state) => {
        return { ...state, errors: {} };
      });
    } else {
      setState((state) => {
        const errors: AnyObject = { ...state.errors };
        delete errors[field];

        return { ...state, errors };
      });
    }
  }

  return {
    get hasMadeRequest() {
      return state.requestsMade > 1;
    },
    get loading() {
      return state.loaders > 0;
    },
    get errors() {
      return state.errors;
    },
    request,
    hasErrors,
    clearErrors,
  };
}

export class ApiError extends Error {
  status: number | undefined;
  errors: KeyValueErrors;
  responseData: AnyObject | undefined;
  context: AnyObject;
  report: boolean;

  constructor(message: string, options: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.errors = options.errors ?? {};
    this.responseData = options.responseData;
    this.context = options.context ?? {};
    this.report = options.report ?? true;

    console.warn(message, this.status, JSON.stringify(this.context));

    const captureStackTrace = (
      Error as ErrorConstructor & {
        captureStackTrace?: (targetObject: object, constructorOpt?: typeof ApiError) => void;
      }
    ).captureStackTrace;

    if (captureStackTrace) {
      captureStackTrace(this, ApiError);
    }
  }

  notifyOnServerError() {
    if (this.status && this.status >= 500) {
      notify.error(this.message, 'use-api.ts - notifyOnServerError');
    }
  }
}
